import React, { useState, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Tooltip from 'rc-tooltip';
import styled, { createGlobalStyle } from 'styled-components';

import Axios from 'axios';
import { sumBy } from 'lodash';
import { QuestionCircleFilled, CloseOutlined } from '@ant-design/icons';
import Modal, { ModalProvider, BaseModalBackground } from 'styled-react-modal';
import { Button, SecondaryButton } from '../styles';
import { Flex, Text, ActionNote } from '../components';
import DirectDepositForm from './DirectDepositForm';
import AuthorizationPage from './AuthorizationPage';
import { PageTitle } from '../biz-components/page-title';

const TooltipStyles = createGlobalStyle`
.rc-tooltip {
  opacity: 1;
  .rc-tooltip-arrow {
    display: none;
  }
  .rc-tooltip-inner {
    background-color: white;
    padding: 12px 12px 20px 12px;
  }
}
`;

const StyledModal = Modal.styled`
  width: 25rem;
  height: 10rem;
  display: flex;
  border-radius: 5px;
  padding: 10px;
  align-items: center;
  justify-content: center;
  background-color: white;
  opacity: ${(props) => props.opacity};
  transition : all 0.3s ease-in-out;
`;

const FadingBackground = styled(BaseModalBackground)`
  opacity: ${(props) => props.opacity};
  transition: all 0.3s ease-in-out;
`;

function EmployeeFillForm(props) {
  const innerIsOnboardingV2 = isOnboardingV2 === 'true';

  const {
    action: { employee_direct_deposits, status },
    employee_onboarding_module,
    companyStaff,
    previewMode = false,
    companyName,
  } = props;

  const required = employee_onboarding_module.config
    ? employee_onboarding_module.config.required
    : true;

  const allowMultipleAccounts =
    employee_onboarding_module.config &&
    employee_onboarding_module.config.allow_multiple_accounts !== undefined
      ? employee_onboarding_module.config.allow_multiple_accounts
      : true;

  let initAccounts = [{ id: Date.now(), account_type: 'checking', percentage: '100%' }];
  if (employee_direct_deposits && !_.isEmpty(employee_direct_deposits)) {
    initAccounts = employee_direct_deposits.map((a, i) => {
      a.id = a.id ? a.id : Date.now() + i; // Prepare a unique id for form input
      return a;
    });
  }
  const defaultValues = { accounts: initAccounts };

  const methods = useForm({ mode: 'onBlur', defaultValues });
  const { handleSubmit, setError, reset } = methods;

  const [ddrFormValues, setDDRFormValues] = useState(defaultValues);

  const [showAuthorizationPage, setShowAuthorizationPage] = useState(false);

  useEffect(() => {
    if (!showAuthorizationPage) {
      reset(ddrFormValues);
    }
  }, [showAuthorizationPage]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveDataError, setSaveDataError] = useState('');

  const [isModalOpen, setisModalOpen] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [showUsingPaycardTooltip, setShowUsingPaycardTooltip] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const handleTooltip = (e) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setShowUsingPaycardTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleTooltip);

    return () => {
      document.removeEventListener('mousedown', handleTooltip);
    };
  }, []);

  function ActivateModalComponent(disabled) {
    function toggleModal(e) {
      e.preventDefault();
      setOpacity(0);
      setisModalOpen(!isModalOpen);
    }

    function afterOpen() {
      setTimeout(() => {
        setOpacity(1);
      }, 100);
    }

    function beforeClose() {
      return new Promise((resolve) => {
        setOpacity(0);
        setTimeout(resolve, 300);
      });
    }

    return (
      <div>
        {!required && (
          <SecondaryButton
            disabled={disabled}
            style={{ marginRight: '5px' }}
            onClick={toggleModal}
            data-testid='skip-direct-deposit-btn'
          >
            Skip
          </SecondaryButton>
        )}
        <StyledModal
          isOpen={isModalOpen}
          afterOpen={afterOpen}
          beforeClose={beforeClose}
          onBackgroundClick={toggleModal}
          onEscapeKeydown={toggleModal}
          opacity={opacity}
          backgroundProps={{ opacity }}
        >
          <Flex column>
            <Flex row style={{ marginBottom: '30px' }}>
              <Flex style={{ padding: '10px' }}>
                <QuestionCircleFilled style={{ fontSize: '24px', color: '#FFBF00' }} />
              </Flex>
              <Flex style={{ padding: '10px' }}>
                <Text fontSize='14px' bold style={{ color: 'rgba(0, 0, 0, 0.65)' }}>
                  Confirm to skip the Direct Deposit Form?
                </Text>
              </Flex>
            </Flex>
            <Flex row justifyContent='flex-end'>
              <SecondaryButton onClick={toggleModal} style={{ marginRight: '10px' }}>
                Cancel
              </SecondaryButton>
              <Button onClick={handleSkipDeposit} data-testid='confirm-skip-direct-deposit-btn'>
                Confirm
              </Button>
            </Flex>
          </Flex>
        </StyledModal>
      </div>
    );
  }

  const handleSkipDeposit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const config = {
      'Access-Control-Allow-Origin': '*',
    };
    const {
      action: { digest_key: actionKey },
    } = props;
    const completeApiURL = `${APIHOST}/v1/onboarding/employee/employee_onboarding_actions/:digest_key/skip_direct_deposit`;
    Axios.put(completeApiURL.replace(':digest_key', actionKey), {}, config)
      .then(() => {
        if (props.onSubmitFormSuccess) {
          props.onSubmitFormSuccess();
        }
      })
      .catch(() => {
        setIsSubmitting(false);
      });
  };

  const validateForm = (data) => {
    let passed = true;
    const { accounts } = data;
    if (accounts) {
      const percentageSum = sumBy(accounts, (account) =>
        parseInt(account.percentage.replace('%', ''), 10)
      );

      if (percentageSum !== 100) {
        setError(`accounts.${accounts.length - 1}.percentage`, {
          type: 'manual',
          message: 'Sum of paycheck % must be 100',
          shouldFocus: true,
        });
        passed = false;
      }
      const accountNumbers = accounts.map(
        (account) => `${account.routing_number}-${account.account_number}`
      );
      accounts.forEach((account, index) => {
        if (accountNumbers.filter((item) => item === accountNumbers[index]).length > 1) {
          passed = false;
          setError(`accounts.${index}.account_number`, {
            type: 'manual',
            message: 'Duplicate account number',
            shouldFocus: true,
          });
        }
      });
    }
    return passed;
  };

  const handleServerError = (error) => {
    let errorMessage = 'Something went wrong while saving data.';
    if (error && error.response && error.response.data) {
      const { error_code: errorCode, message } = error.response.data;
      errorMessage = `(${errorCode}) ${message}`;
    }
    setSaveDataError(errorMessage);
    setIsSubmitting(false);
  };

  const submitForm = (data) => {
    if (!validateForm(data)) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      employee: {
        direct_deposits: data.accounts.map((account) => ({
          ...account,
          percentage: account.percentage.replace('%', ''),
        })),
      },
    };

    const {
      action: { digest_key: actionKey },
    } = props;
    const submitApiURL = `${APIHOST}/v1/onboarding/direct_deposit/employee/actions/:digest_key`;
    const completeApiURL = `${APIHOST}/v1/onboarding/employee/employee_onboarding_actions/:digest_key/completed`;

    Axios.put(submitApiURL.replace(':digest_key', actionKey), payload)
      .then(() => {
        Axios.put(completeApiURL.replace(':digest_key', actionKey), {})
          .then(() => {
            setIsSubmitting(false);
            if (props.onSubmitFormSuccess) {
              props.onSubmitFormSuccess();
            }
          })
          .catch((error) => {
            setIsSubmitting(false);
            handleServerError(error);
          });
      })
      .catch((error) => {
        setIsSubmitting(false);
        handleServerError(error);
      });
  };

  const renderSubmitButtons = () => {
    let disabled = false;
    let disableNote = '';
    const {
      action: { status: actionStatus },
      companyStaff,
      previewMode = false,
    } = props;

    if (previewMode) {
      // display the action anyway
      disabled = true;
      disableNote = 'Cannot submit form in preview mode.';
    } else if (
      actionStatus !== 'not_started' &&
      actionStatus !== 'in_progress' &&
      actionStatus !== 'redo'
    ) {
      disabled = true;
      disableNote = 'Form has been submitted.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disableNote = 'This form should be submitted by employee.';
    }

    return (
      <Flex column alignItems='flex-end'>
        <Flex row>
          <ModalProvider backgroundComponent={FadingBackground}>
            <div className='skip-content'>{ActivateModalComponent(disabled)}</div>
          </ModalProvider>
          <Button
            disabled={disabled}
            data-testid='direct-deposit-submit-btn'
            onClick={() => {
              handleSubmit((data) => {
                if (validateForm(data)) {
                  setDDRFormValues(data);
                  setShowAuthorizationPage(true);
                }
              })();
            }}
          >
            Submit
          </Button>
        </Flex>
        {disabled && <ActionNote text={disableNote} />}
      </Flex>
    );
  };
  const moduleName = props.action.module_name;

  return (
    <Flex column>
      <FormProvider {...methods}>
        {innerIsOnboardingV2 && <PageTitle title={moduleName} />}
        {showAuthorizationPage ? (
          <AuthorizationPage
            isSubmitting={isSubmitting}
            companyName={companyName}
            onClickAuthorize={() => {
              submitForm(ddrFormValues);
            }}
            saveDataError={saveDataError}
            onClickBack={() => {
              setSaveDataError('');
              setShowAuthorizationPage(false);
            }}
          />
        ) : (
          <>
            <Text variant='title'>Direct Deposit Authorization Form</Text>
            <Text variant='subtitle' mt='5px'>
              {allowMultipleAccounts
                ? 'Please enter this information carefully, as minor errors may cause delays in processing paychecks. You can deposit and split your wages across multiple bank accounts (up to 3)'
                : 'Please enter this information carefully, as minor errors may cause delays in processing paychecks.'}
            </Text>
            <TooltipStyles />

            <Tooltip
              visible={showUsingPaycardTooltip}
              trigger='click'
              placement='bottom'
              overlay={
                <Flex column style={{ width: '360px' }}>
                  <Flex justifyContent='space-between'>
                    <Text
                      style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#464D5D',
                      }}
                    >
                      Using paycards?
                    </Text>
                    <CloseOutlined
                      style={{ color: '#464D5D', cursor: 'pointer', fontSize: '16px' }}
                      onClick={() => {
                        setShowUsingPaycardTooltip(false);
                      }}
                    />
                  </Flex>
                  <Text
                    style={{
                      color: '#68728A',
                      fontSize: '14px',
                    }}
                  >
                    If you’re receiving funds to a paycard, please select checking account and reach
                    out to your paycard provider to get the routing and account numbers.
                  </Text>
                </Flex>
              }
            >
              <Text
                style={{
                  color: '#3A67D4',
                  cursor: 'pointer',
                  marginTop: '20px',
                  fontSize: '14px',
                  display: 'block',
                  width: 'fit-content',
                }}
                ref={tooltipRef}
                onClick={() => {
                  setShowUsingPaycardTooltip(true);
                }}
              >
                Using paycards?
              </Text>
            </Tooltip>
            <img
              src='/j/images/employee-onboarding/check-sample.svg'
              style={{ width: '276px', marginTop: '20px' }}
            />

            <DirectDepositForm
              allowMultipleAccounts={allowMultipleAccounts}
              disabled={(companyStaff && companyStaff.id) || previewMode || isModalOpen}
              isCompleted={status === 'completed'}
            />
            {renderSubmitButtons()}
          </>
        )}
      </FormProvider>
    </Flex>
  );
}

export default EmployeeFillForm;
